import { useState, useRef, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { isToday, isTomorrow, isPeriodPastToday } from '../components/WhenDropdown/whenDropdownUtils'
import { escapeBelongsToTaskDetails } from '../utils/inlineEscape'
import { parseStartDate } from '../utils/parseStartDate'
import type { StartPeriod } from '@/src/renderer/lib/db/models'

export interface DateTimeValue {
  date: Date
  time: string | null
  period: StartPeriod | null
}

interface UseDateTimeDropdownStateOptions {
  value: DateTimeValue | null
  onChange: (value: DateTimeValue) => void
  onClose: () => void
  /** Format the current draft state for the NL input display */
  formatDisplay: (value: DateTimeValue, locale: string) => string
  /**
   * Whether the NL input grabs focus on mount. Default `true` (the popover
   * behaviour). `false` in the Task Details' `inline` variant, where seven
   * contents are open at once and only one of them may win the focus.
   */
  autoFocus?: boolean
  /**
   * Ob der Kasten offen in den **Aufgaben-Details** steht. Dann gehört Escape der Spalte,
   * nicht dem Kasten — siehe {@link escapeBelongsToTaskDetails}.
   */
  inline?: boolean
}

/** Der Wert als Zeichenkette — zum Vergleichen, nicht zum Anzeigen. */
const stampOf = (value: DateTimeValue | null): string =>
  value ? `${value.date.getTime()}|${value.time ?? ''}|${value.period ?? ''}` : ''

/** Nur eine VOLLSTÄNDIGE Uhrzeit wird geschrieben; „14:3" ist noch ein Entwurf. */
const completeTime = (time: string): string | null =>
  /^\d{1,2}:\d{2}$/.test(time) ? time : null

/** Woher der Tag kommt — Schnellwahl, Kalender oder (mit Zeitraum) keins von beiden. */
const daySourceOf = (value: DateTimeValue | null): 'quick' | 'calendar' | null => {
  if (!value?.date || value.period) return null
  return isToday(value.date) || isTomorrow(value.date) ? 'quick' : 'calendar'
}

/**
 * Shared state management for date/time dropdown components (WhenDropdown, DeadlineDropdown).
 *
 * Encapsulates:
 * - Draft state (selectedDate, selectedPeriod, selectedTime, daySource, timeSource)
 * - Day quick-pick / calendar selection
 * - Period selection with auto-date and toggle
 * - Time value application and clearing
 * - Confirm logic
 * - NL input parsing and keyboard shortcuts (Cmd+1-6, Enter, Escape)
 * - inputValue management for the NL display field
 *
 * **In `inline` (Aufgaben-Details) gibt es keinen Entwurf, der auf ein
 * „Bestätigen" wartet** (ADR 0026): Jede Änderung wird sofort geschrieben, und
 * ein Wert, der von außen kommt, wird übernommen — siehe `write` und den
 * Übernahme-Effekt weiter unten.
 */
export function useDateTimeDropdownState({
  value,
  onChange,
  onClose,
  formatDisplay,
  autoFocus = true,
  inline = false,
}: UseDateTimeDropdownStateOptions) {
  const { i18n } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)

  // Focus the NL input on mount. autoFocus is unreliable in floating-ui portals
  // because the portal content starts as visibility:hidden while positioning runs.
  useEffect(() => {
    if (!autoFocus) return
    const timer = setTimeout(() => { inputRef.current?.focus() }, 50)
    return () => clearTimeout(timer)
  }, [autoFocus])

  // ── Internal draft state ──
  const [selectedDate, setSelectedDate] = useState<Date | null>(value?.date ?? null)
  const [selectedPeriod, setSelectedPeriod] = useState<StartPeriod | null>(value?.period ?? null)
  const [selectedTime, setSelectedTime] = useState<string>(value?.time ?? '')
  const [daySource, setDaySource] = useState<'quick' | 'calendar' | null>(null)
  const [timeSource, setTimeSource] = useState<'period' | 'time' | null>(
    value?.period ? 'period' : value?.time ? 'time' : null
  )
  const [inputValue, setInputValue] = useState<string | null>(null)

  // Initialize daySource from existing value
  useEffect(() => {
    setDaySource(daySourceOf(value))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * **Der zuletzt geschriebene ODER übernommene Wert.** Er hält zwei Dinge
   * auseinander, die am `value` gleich aussehen: den eigenen Wert, der nach dem
   * Schreiben zurückkommt, und einen fremden (Entfernen, Marke, Aufgaben-Menü,
   * Rückgängig).
   */
  const lastWritten = useRef(stampOf(value))

  /**
   * **Sofort schreiben — nur in `inline`.**
   *
   * Überall sonst bleibt es beim Entwurf: Dort schließt „Bestätigen" den
   * Kasten, und ein Schreiben bei jedem Klick ließe die Liste unter dem
   * offenen Kasten umspringen.
   *
   * Derselbe Wert geht nur einmal hinaus. Ein Doppelklick ist Klick, Klick,
   * Doppelklick — ohne die Sperre lägen drei gleiche Einträge in der
   * Warteschlange.
   */
  const write = useCallback((next: DateTimeValue) => {
    if (!inline) return
    const stamp = stampOf(next)
    if (stamp === lastWritten.current) return
    lastWritten.current = stamp
    onChange(next)
  }, [inline, onChange])

  /*
    **Ein Wert von außen wird übernommen.**

    Bis zur Etappe 2 baute `TaskDetails` den Bereich dafür neu auf
    (Fingerabdruck des Werts als `key`). Mit dem Sofort-Schreiben geht das
    nicht mehr: JEDE eigene Änderung wäre ein neuer Fingerabdruck, und der
    Neuaufbau risse den Caret mitten im Tippen aus dem Zeitfeld.

    Der eigene Wert, der zurückkommt, ist kein fremder: Er steht in
    `lastWritten` und lässt den Entwurf in Ruhe — auch eine halb getippte
    Uhrzeit, die dem Geschriebenen schon wieder voraus ist.
  */
  const valueStamp = stampOf(value)
  useEffect(() => {
    if (!inline || valueStamp === lastWritten.current) return
    lastWritten.current = valueStamp
    setSelectedDate(value?.date ?? null)
    setSelectedPeriod(value?.period ?? null)
    setSelectedTime(value?.time ?? '')
    setTimeSource(value?.period ? 'period' : value?.time ? 'time' : null)
    setDaySource(daySourceOf(value))
    setInputValue(null)
  }, [inline, valueStamp]) // eslint-disable-line react-hooks/exhaustive-deps

  const hasSelection = selectedDate !== null

  // ── Display text for NL input ──
  const displayText = (() => {
    if (!selectedDate) return ''
    return formatDisplay(
      { date: selectedDate, time: selectedTime || null, period: selectedPeriod },
      i18n.language,
    )
  })()

  // ── Day selection ──
  const handleQuickDay = useCallback((offset: number) => {
    const d = new Date()
    d.setDate(d.getDate() + offset)
    d.setHours(0, 0, 0, 0)
    setSelectedDate(d)
    setDaySource('quick')
    setSelectedPeriod(null)
    setSelectedTime('')
    setTimeSource(null)
    setInputValue(null)
    write({ date: d, time: null, period: null })
  }, [write])

  const handleCalendarDay = useCallback((date: Date) => {
    setSelectedDate(date)
    setDaySource('calendar')
    setInputValue(null)
    // Der Tag wechselt, die Zeit bleibt — wie im Entwurf auch.
    write({ date, time: selectedPeriod ? null : completeTime(selectedTime), period: selectedPeriod })
  }, [write, selectedPeriod, selectedTime])

  // ── Period selection ──
  const handlePeriodSelect = useCallback((period: StartPeriod) => {
    if (selectedPeriod === period && timeSource === 'period') {
      setSelectedPeriod(null)
      setTimeSource(null)
      setInputValue(null)
      if (selectedDate && (isToday(selectedDate) || isTomorrow(selectedDate))) {
        setDaySource('quick')
      }
      if (selectedDate) write({ date: selectedDate, time: null, period: null })
      return
    }

    setSelectedPeriod(period)
    setSelectedTime('')
    setTimeSource('period')
    setInputValue(null)

    const shouldUseTomorrow = isPeriodPastToday(period) && (!selectedDate || isToday(selectedDate))
    let date = selectedDate
    if (!date || shouldUseTomorrow) {
      date = new Date()
      if (shouldUseTomorrow) date.setDate(date.getDate() + 1)
      date.setHours(0, 0, 0, 0)
      setSelectedDate(date)
    }
    if (daySource === 'quick') {
      setDaySource(null)
    }
    write({ date, time: null, period })
  }, [selectedDate, daySource, selectedPeriod, timeSource, write])

  // ── Time value application ──
  const applyTimeValue = useCallback((val: string) => {
    if (val) {
      setSelectedPeriod(null)
      setTimeSource('time')
      setInputValue(null)
      let date = selectedDate
      if (!date) {
        date = new Date()
        date.setHours(0, 0, 0, 0)
        setSelectedDate(date)
      }
      write({ date, time: val, period: null })
    } else {
      setTimeSource(null)
      setInputValue(null)
      if (selectedDate) write({ date: selectedDate, time: null, period: selectedPeriod })
    }
  }, [selectedDate, selectedPeriod, write])

  const handleTimeClear = useCallback(() => {
    setSelectedTime('')
    setTimeSource(selectedPeriod ? 'period' : null)
    setInputValue(null)
    if (selectedDate) write({ date: selectedDate, time: null, period: selectedPeriod })
    // In den Aufgaben-Details gehört der Fokus der Liste, bis jemand hineingreift —
    // das Wertfeld holt ihn sich dort nicht.
    if (!inline) setTimeout(() => { inputRef.current?.focus() }, 50)
  }, [selectedPeriod, selectedDate, write, inline])

  // ── Confirm ──
  const handleConfirm = useCallback(() => {
    if (!selectedDate) return
    // In `inline` ist alles schon geschrieben; die Eingabetaste holt höchstens
    // nach, was noch aussteht (`write` lässt denselben Wert nicht zweimal hinaus).
    if (inline) {
      write({ date: selectedDate, time: selectedPeriod ? null : completeTime(selectedTime), period: selectedPeriod })
      return
    }
    onChange({
      date: selectedDate,
      time: selectedTime || null,
      period: selectedPeriod,
    })
    onClose()
  }, [selectedDate, selectedTime, selectedPeriod, onChange, onClose, inline, write])

  // ── Shortcut helper ──
  const shortcutAndReset = useCallback((fn: () => void) => {
    fn()
    setInputValue(null)
  }, [])

  // ── NL input keyboard ──
  const handleInputKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    // Vor dem `stopPropagation`: In den Aufgaben-Details muss Escape hinaus zur Spalte.
    if (escapeBelongsToTaskDetails(e, inline)) return
    e.stopPropagation()

    if (e.key === 'Enter') {
      e.preventDefault()
      if (inputValue && inputValue.trim()) {
        const parsed = parseStartDate(inputValue, i18n.language)
        if (parsed) {
          onChange({
            date: parsed.date,
            time: parsed.time ?? null,
            period: parsed.period,
          })
          onClose()
        }
      } else if (hasSelection) {
        handleConfirm()
      }
      return
    }

    if (e.key === 'Escape') {
      e.preventDefault()
      onClose()
      return
    }

    if (e.metaKey || e.ctrlKey) {
      if (e.key === '1') { e.preventDefault(); shortcutAndReset(() => handleQuickDay(0)); return }
      if (e.key === '2') { e.preventDefault(); shortcutAndReset(() => handleQuickDay(1)); return }
      if (e.key === '3') { e.preventDefault(); shortcutAndReset(() => handlePeriodSelect('morning')); return }
      if (e.key === '4') { e.preventDefault(); shortcutAndReset(() => handlePeriodSelect('noon')); return }
      if (e.key === '5') { e.preventDefault(); shortcutAndReset(() => handlePeriodSelect('afternoon')); return }
      if (e.key === '6') { e.preventDefault(); shortcutAndReset(() => handlePeriodSelect('evening')); return }
    }
  }, [inline, inputValue, i18n.language, hasSelection, onClose, handleQuickDay, handlePeriodSelect, handleConfirm, shortcutAndReset, onChange])

  // ── Global keyboard ──
  const handleGlobalKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.metaKey || e.ctrlKey) {
      if (e.key === '1') { e.preventDefault(); shortcutAndReset(() => handleQuickDay(0)); return }
      if (e.key === '2') { e.preventDefault(); shortcutAndReset(() => handleQuickDay(1)); return }
      if (e.key === '3') { e.preventDefault(); shortcutAndReset(() => handlePeriodSelect('morning')); return }
      if (e.key === '4') { e.preventDefault(); shortcutAndReset(() => handlePeriodSelect('noon')); return }
      if (e.key === '5') { e.preventDefault(); shortcutAndReset(() => handlePeriodSelect('afternoon')); return }
      if (e.key === '6') { e.preventDefault(); shortcutAndReset(() => handlePeriodSelect('evening')); return }
    }
    if (e.key === 'Enter' && hasSelection) {
      const target = e.target as HTMLElement
      if (target === inputRef.current) return
      e.preventDefault()
      handleConfirm()
    }
  }, [hasSelection, handleConfirm, handleQuickDay, handlePeriodSelect, shortcutAndReset])

  return {
    inputRef,
    selectedDate,
    selectedPeriod,
    selectedTime,
    setSelectedTime,
    daySource,
    timeSource,
    inputValue,
    setInputValue,
    hasSelection,
    displayText,
    handleQuickDay,
    handleCalendarDay,
    handlePeriodSelect,
    applyTimeValue,
    handleTimeClear,
    handleConfirm,
    handleInputKeyDown,
    handleGlobalKeyDown,
  }
}
